/**
 * 노후화 분석 (DESIGN §8.11).
 *
 * **묻는 것은 둘이다.** "이 충전기는 늙어서 나빠지고 있는가" — 그리고 **그것을 이
 * 데이터로 말할 수 있는가.** 후자를 먼저 답하는 것이 이 화면의 첫 일이다. 그래서 맨
 * 위가 결론이 아니라 지표 산출 가능성 표다.
 *
 * 원천 두 개를 나란히 두되 섞지 않는다. 공개 API(전국·상태코드)와 환경공단 통합관리시스템
 * (환경부 직접설치분·알람코드·충전량·철거일)은 범위도 정의도 다르다 — 결합 키는 100% 맞지만
 * (§8.11.3) 숫자를 한 그래프에 섞으면 어느 정의의 값인지 잃는다.
 *
 * **하지 않는 것:** 수명 예측·잔존가치·교체 시점 산정. 철거 사유가 없어 노후 철거와 사업
 * 종료 철거를 가를 수 없다. 전압·전류 지표 — 어느 원천에도 없다.
 */

import { Section } from '@/components/Section'
import { useObservationDays } from '@/lib/window'
import { IndicatorMatrix } from './deterioration/IndicatorMatrix'
import { Bathtub, PowerTrend, Stress, Survival } from './deterioration/KecoWidgets'
import { AgeEffectForest, Removal, YearRates } from './deterioration/PublicWidgets'
import './DeteriorationPage.css'

export function DeteriorationPage() {
  const days = useObservationDays()
  return (
    <div className="det">
      <header className="det__intro">
        <p className="det__lead">
          이 화면은 <strong>노후를 판정하기 전에, 판정할 수 있는지부터</strong> 묻습니다. 공개
          API 의 고장률로 본 노후는 지금까지 <strong>검출되지 않았습니다</strong> — 원인은 날짜가
          부족해서가 아니라 연식이 사업자·제조사 구성을 대신하고(교란), 상태코드가 고장 사건이
          아니며(결과변수), 노후기는 고장 난 채 남지 않고 철거되기(생존편향) 때문입니다.
        </p>
        <p className="det__aside">
          <strong>원천 두 개.</strong> 공개 API — 전국 52만 기, 상태코드, 관측{' '}
          {days != null ? <span className="num">{days}일</span> : '—'}. 환경공단 통합관리시스템 —
          환경부 직접설치 약 1.1만 기, 알람코드·충전량·철거일, 2024-01부터. 두 원천은{' '}
          <code>STAT_ID-CHGER_ID</code> 로 100% 결합되지만 정의가 달라 섞지 않고 나란히 둡니다.
          모든 값은 빌드·집계가 미리 계산한 전국 값이라 <strong>왼쪽 필터를 타지 않습니다.</strong>
        </p>
      </header>

      <Section
        index={1}
        title="먼저, 무엇을 말할 수 있는가"
        lead="KTC 데이터 요청서의 노후 지표 10종을 원천별로 — 지금 낼 수 있는 것, 대리값만 있는 것, 막힌 것과 그 이유."
        layout="full"
      >
        <IndicatorMatrix />
      </Section>

      <Section
        index={2}
        title="공개 API — 연식 효과는 통제해도 남는가"
        lead="방안 1·3. 연식별로 그냥 견주면 오래될수록 나빠 보인다. 같은 사업자·타입·제조사 안에서 견주면 그 차이가 얼마나 남는지가 노후의 몫이다."
      >
        <AgeEffectForest />
        <YearRates />
      </Section>

      <Section
        index={3}
        title="공개 API — 무엇이 사라지는가"
        lead="방안 2. 노후기는 고장 난 채 남지 않고 목록에서 사라진다. 사라짐은 공개 데이터에서 가장 강한 노후 신호지만 사유가 없다."
        layout="full"
      >
        <Removal />
      </Section>

      <Section
        index={4}
        title="환경공단 — 경과년수에 따라 고장과 철거가 어떻게 변하는가"
        lead="방안 4·2. 고장코드가 있는 원천이라 설비 기인 지속고장을 셀 수 있고, 철거일이 있어 생존곡선을 그릴 수 있다."
      >
        <Bathtub />
        <Survival />
      </Section>

      <Section
        index={5}
        title="환경공단 — 고장 이전의 열화와 사용의 무게"
        lead="방안 5·6. 고장이 나기 전에 출력이 먼저 떨어지는가, 많이 쓰인 설비가 더 고장나는가."
      >
        <PowerTrend />
        <Stress />
      </Section>

      <aside className="det__rules" aria-label="해석 규칙">
        <h2 className="det__rulestitle">이 화면에서 하지 않는 해석</h2>
        <ul>
          <li>
            <strong>2025-03 전·후 절대 비교</strong> — 환경공단 알람 수집 정책이 바뀌어 알람 총량이
            약 1/6 로 줄었습니다.
          </li>
          <li>
            <strong>MTBF 절대값</strong> — 분모(가동 시간)에 계획정지 구분이 없고, 알람 해제가
            복구를 뜻한다는 보장이 없습니다.
          </li>
          <li>
            <strong>최근 월의 감소를 개선으로</strong> — 아직 끝나지 않은 고장이 덜 세어진
            것입니다(우측 절단).
          </li>
          <li>
            <strong>사라짐·철거를 수명 종료로</strong> — 사업 종료·부지 반환·고출력 전환도 같은
            모양입니다.
          </li>
          <li>
            <strong>수명 예측·잔존가치·교체 시점</strong> — 철거 사유와 설비 단위 설치·교체
            이력(요청서 A·F 계층)이 있어야 합니다.
          </li>
        </ul>
        <p className="det__rulesfoot">
          출처: 노후지표 해석가이드 §7 · KTC 데이터 요청서 v1.0 · DESIGN §8.11.
        </p>
      </aside>
    </div>
  )
}
